import { useState } from 'react';

type Mark = 'good' | 'mid' | 'bad';
type Cell = [Mark, string];

const PROVIDERS = [
  { key: 'us', name: 'Canadawale Yar', us: true },
  { key: 'airalo', name: 'Airalo', us: false },
  { key: 'holafly', name: 'Holafly', us: false },
  { key: 'silent', name: 'Silent.Link', us: false },
] as const;

type ProviderKey = (typeof PROVIDERS)[number]['key'];

const ROWS: { label: string; v: Record<ProviderKey, Cell> }[] = [
  { label: 'Account needed', v: { us: ['good', 'None'], airalo: ['bad', 'Required'], holafly: ['bad', 'Required'], silent: ['good', 'None'] } },
  { label: 'Email collected', v: { us: ['good', 'Account email only'], airalo: ['bad', 'Verified email'], holafly: ['bad', 'Required'], silent: ['good', 'None'] } },
  { label: 'ID / KYC', v: { us: ['good', 'Never'], airalo: ['mid', 'Some destinations'], holafly: ['mid', 'Generally no'], silent: ['good', 'Never'] } },
  { label: 'IP routing disclosed', v: { us: ['good', 'Yes, every plan'], airalo: ['bad', 'Not disclosed'], holafly: ['bad', 'Not disclosed'], silent: ['mid', 'Limited info'] } },
  { label: 'Privacy label per plan', v: { us: ['good', '“Max Privacy” badges'], airalo: ['bad', 'No'], holafly: ['bad', 'No'], silent: ['bad', 'No'] } },
];

const PREFIX: Record<Mark, string> = { good: 'Yes: ', mid: 'Partly: ', bad: 'No: ' };

function scoreOf(key: ProviderKey): number {
  return ROWS.filter((row) => row.v[key][0] === 'good').length;
}

function MarkIcon({ mark }: { mark: Mark }) {
  if (mark === 'mid') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" aria-hidden>
        <path d="M6 12h12" />
      </svg>
    );
  }
  if (mark === 'bad') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function EsimCompare() {
  const peers = PROVIDERS.filter((provider) => !provider.us);
  const [activePeer, setActivePeer] = useState<ProviderKey>(peers[0].key);
  const total = ROWS.length;

  return (
    <section className="esim-compare" aria-labelledby="esim-cmp-title">
      <p className="esim-eyebrow">How we compare</p>
      <h2 id="esim-cmp-title">Know what you’re buying, and what it knows about you.</h2>
      <p className="esim-lead">A side-by-side look at what each eSIM provider asks of you before you can connect.</p>
      <div className="esim-peer-tabs" role="group" aria-label="Compare with">
        <span>Compare with</span>
        {peers.map((provider) => (
          <button
            key={provider.key}
            type="button"
            aria-pressed={provider.key === activePeer}
            onClick={() => setActivePeer(provider.key)}
          >
            {provider.name}
          </button>
        ))}
      </div>
      <div className="esim-cmp-card">
        <div className="esim-cmp-scroll">
          <table className="esim-cmp">
            <caption className="esim-sr">Privacy comparison of eSIM providers</caption>
            <thead>
              <tr>
                <th scope="col"><span className="esim-sr">Feature</span></th>
                {PROVIDERS.map((provider) => {
                  const score = scoreOf(provider.key);
                  const hidden = !provider.us && provider.key !== activePeer;
                  return (
                    <th key={provider.key} scope="col" className={provider.us ? 'is-us' : hidden ? 'is-peer-off' : undefined}>
                      <div className="esim-prov">
                        {provider.us ? <span className="esim-rec">Recommended</span> : null}
                        <b>{provider.name}</b>
                        <span className="esim-score">
                          <span className="esim-pips" aria-hidden="true">
                            {Array.from({ length: total }, (_, index) => (
                              <i key={index} className={index < score ? 'is-on' : undefined} />
                            ))}
                          </span>
                          {score} of {total} checks
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label}>
                  <th scope="row">{row.label}</th>
                  {PROVIDERS.map((provider) => {
                    const [mark, text] = row.v[provider.key];
                    const hidden = !provider.us && provider.key !== activePeer;
                    return (
                      <td key={provider.key} className={provider.us ? 'is-us' : hidden ? 'is-peer-off' : undefined}>
                        <div className="esim-cell">
                          <span className={`esim-ic is-${mark}`} aria-hidden="true"><MarkIcon mark={mark} /></span>
                          <span><span className="esim-sr">{PREFIX[mark]}</span>{text}</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="esim-cmp-foot">
        <span>Based on each provider’s public information; policies change.</span>
        <a href="#esim-plans">Back to plans ↑</a>
      </div>
    </section>
  );
}
